// Datenformen, die Server und Browser teilen.

export interface Company {
  owner: string;
  [key: string]: unknown;
}

export interface ClientLink {
  label: string;
  url: string;
}

export interface Client {
  id: string;
  name: string;
  short: string;
  contact: string;
  email: string;
  phone: string;
  street: string;
  zip: string;
  city: string;
  color: string;
  status: string;
  tabs: string[];
  links: ClientLink[];
  [key: string]: unknown;
}

export interface Project {
  id: string;
  client: string;
  title: string;
  phase: string;
  /** ISO-Datum oder null */
  due: string | null;
  [key: string]: unknown;
}

export interface Note {
  id: string;
  text: string;
  kind: "note" | "todo";
  done: boolean;
  /** Zeitstempel in Millisekunden */
  at: number;
  /** To-do: wann ich es mache (YYYY-MM-DD), optional von–bis (HH:MM) */
  day?: string | null;
  from?: string | null;
  to?: string | null;
  /** To-do: optional zu einem Projekt dieses Kunden */
  project?: string | null;
}

export interface SheetTable {
  source: "gsheet" | "excel";
  id?: string;
  url: string;
  name: string;
  uploadUrl?: string;
  addedAt?: string;
}

export interface State {
  company: Company;
  clients: Client[];
  projects: Project[];
  notes: Record<string, Note[]>;
  tables: Record<string, SheetTable>;
}

export type StateKey = keyof State;
export const STATE_KEYS: StateKey[] = ["company", "clients", "projects", "notes", "tables"];

export type Role = "admin" | "member";

export interface PublicUser {
  id: string;
  email: string;
  name: string;
  role: Role;
}

export interface Me {
  user: PublicUser;
  /** 1 = Passwort geprüft, 2 = mit 2FA-Code */
  aal: 1 | 2;
  totpEnabled: boolean;
  mustChangePassword: boolean;
}

export interface UserRow extends PublicUser {
  totpEnabled: boolean;
  disabled: boolean;
  mustChangePassword: boolean;
  createdAt: string;
  lastSeenAt: string | null;
}

export interface ApiTokenRow {
  id: string;
  name: string;
  /** Erste Zeichen zum Wiedererkennen */
  prefix: string;
  scope: "read" | "write";
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdBy: string;
}

/** Abrechnung eines Kunden über MILKEE (im Kunden unter «billing» gespeichert). */
export interface ClientBilling {
  milkeeCustomerId: number;
  milkeeCustomerName: string;
  /** CHF pro fertigem Video */
  pricePerVideo: number;
  /** Automatik: jedes Video als Projekt anlegen, bei «Final» abschliessen und verrechnen */
  auto?: boolean;
}

export interface MilkeeOverview {
  year: number;
  income: number[];
  expense: number[];
  incomeTotal: number;
  expenseTotal: number;
  open: { id: number; number: string; customer: string; title: string; total: number; due: string | null; overdue: boolean; status: string }[];
  openTotal: number;
  overdueCount: number;
  fetchedAt: string;
}

/* ---------- MILKEE (nur durchgereicht, nicht gespeichert) ---------- */

export interface MkProject {
  id: number;
  name: string;
  customerId: number | null;
  customerName: string;
  archived: boolean;
  kanbanStatus: string | null;
  type: string;
  hourlyRate: number | null;
  endDate: string | null;
}
export interface MkTime {
  id: number;
  date: string;
  hours: number;
  minutes: number;
  description: string;
  billable: boolean;
  /** open, invoiced, paid */
  status: string;
  projectId: number | null;
  projectName: string;
  customerName: string;
  hourlyRate: number;
  total: number;
  /** gearbeitet von–bis, falls erfasst («13:00») */
  start: string | null;
  end: string | null;
}
export interface MkTimer {
  active: boolean;
  projectId?: number | null;
  startedAt?: string;
  description?: string;
  billable?: boolean;
}
export interface MkDoc {
  id: number;
  kind: "invoice" | "proposal";
  number: string;
  title: string;
  customer: string;
  /** MILKEE-Kunde, um auf den eigenen Kunden zu verlinken */
  customerId: number | null;
  date: string | null;
  /** Rechnung: zahlbar bis, Offerte: gültig bis */
  due: string | null;
  status: string;
  total: number;
  overdue: boolean;
}
/** Auswertung der Buchhaltung für einen Zeitraum (alles aus MILKEE berechnet) */
export interface MkPeriodTotals {
  /** fakturiert: Rechnungen ohne Entwürfe und Stornos, nach Rechnungsdatum */
  invoiced: number;
  /** gebuchte Einnahmen und Ausgaben */
  income: number;
  expense: number;
  profit: number;
  invoiceCount: number;
}
export interface MkCustomerRevenue {
  /** MILKEE-Kunde */
  id: number | null;
  name: string;
  invoiced: number;
  paid: number;
  open: number;
  invoices: number;
  /** Anteil am Umsatz des Zeitraums, 0–1 */
  share: number;
  last: string | null;
}
export interface MkProposalStats {
  draft: { count: number; sum: number };
  sent: { count: number; sum: number };
  accepted: { count: number; sum: number };
  declined: { count: number; sum: number };
  /** angenommen / (angenommen + abgelehnt); null, solange nichts entschieden ist */
  rate: number | null;
}
export interface MkInsights {
  from: string;
  to: string;
  months: { key: string; invoiced: number; income: number; expense: number }[];
  totals: MkPeriodTotals;
  /** gleicher Zeitraum ein Jahr früher */
  prev: MkPeriodTotals;
  margin: number | null;
  avgInvoice: number;
  openTotal: number;
  overdueTotal: number;
  overdueCount: number;
  customers: MkCustomerRevenue[];
  expenseCats: { name: string; sum: number; share: number }[];
  proposals: MkProposalStats;
  fetchedAt: string;
}
/** Finanzen eines Kunden */
export interface MkCustomerFinance {
  customerId: number;
  from: string;
  to: string;
  months: { key: string; invoiced: number }[];
  invoiced: number;
  paid: number;
  open: number;
  overdue: number;
  invoiceCount: number;
  avgInvoice: number;
  /** über alle Jahre */
  allTime: number;
  firstInvoice: string | null;
  proposals: MkProposalStats;
  documents: MkDoc[];
  fetchedAt: string;
}

export interface MkEntry {
  id: number;
  number: string;
  date: string;
  description: string;
  sum: number;
  type: string;
  debit: string;
  credit: string;
  hasFile: boolean;
  customer: string;
}
export interface MkAccount {
  id: number;
  number: number;
  name: string;
  type: "bank" | "income" | "expense" | "asset" | "liability";
  primary: boolean;
}

export interface BilledRow {
  id: string;
  clientId: string;
  itemKey: string;
  title: string;
  amount: number;
  invoiceId: number;
  invoiceNumber: string;
  createdAt: string;
}

export interface NotificationRow {
  id: string;
  title: string;
  body: string;
  link: string;
  tab: string;
  /** schon in der Glocke gesehen */
  seen: boolean;
  createdAt: string;
}

export interface ApiErrorBody {
  code: string;
  message: string;
}

/** Stand der Automatik für ein Video aus der Live-Tabelle. */
export interface AutoVideo {
  itemKey: string;
  projectId: string | null;
  milkeeProjectId: number | null;
  status: string;
  invoiceNumber: string | null;
  closedAt: string | null;
  /** von Hand verrechnet, bevor das Video final ist */
  earlyAt: string | null;
  billedAt: string | null;
  error: string | null;
}

/** PDF in einem Google-Drive-Ordner. */
export interface DrivePdf {
  id: string;
  name: string;
  url: string;
  modified: string | null;
}
/**
 * Drehbuch eines Videos aus dem Drive-Ordner.
 * one = genau ein PDF, chosen = von Hand gewählt, choose = mehrere PDFs, Auswahl offen, declined = keines ist ein Drehbuch.
 */
export interface DriveScriptInfo {
  itemKey: string;
  state: "one" | "chosen" | "choose" | "declined";
  file: DrivePdf | null;
  candidates: DrivePdf[];
}
export interface DriveScriptsResponse {
  items: DriveScriptInfo[];
  /** Ordner, die gelesen werden konnten / die es gab */
  folders: { ok: number; total: number };
  error: { code: string; message: string } | null;
}
